import { useEffectEvent, useLayoutEffect } from 'react'

import { renderAs, type IntrinsicTag, type PolymorphicProps } from '@/components/motion/polymorphic'
import { useLinger } from '@/lib/motion/use-linger'
import { cn } from '@/lib/utils'

/**
 * Which surface is moving.
 *
 * - `menu`: a popover panel's body, and every menu built on Popover. Scales in
 *   from just above at 98% (220ms), from the `transformOrigin` the caller puts
 *   in `style`: the corner nearest its trigger.
 * - `dropdown`: a short list opened inside an already open panel (the date
 *   picker's month and year menus). The same scale-in at the fast step (120ms),
 *   from its top edge.
 * - `toast`: in from the right edge (480ms), out to it faster (220ms).
 * - `modal`: a dialog that slides up from the bottom edge on a phone and
 *   settles in place (8px, 98%, fading) on a desk, over 220ms.
 * - `sheet`: a dialog that slides up from the bottom edge, 220ms.
 *
 * `modal` and `sheet` go on the `<dialog>` itself (`as="dialog"`) and enter on
 * `@starting-style`, not an animation class: an element arriving in the top
 * layer from `display: none` has no before-change style for a transition to
 * move from, forcing a reflow does not give it one, and waiting a frame for a
 * second render only works while the tab is painting. Reduced motion is
 * already killed globally, but these are restated `motion-reduce:` because the
 * movement is their whole entrance.
 */
export type OverlayKind = 'menu' | 'dropdown' | 'toast' | 'modal' | 'sheet'

interface Exit {
  className: string
  /** How long the exit runs: the surface stays mounted this long once it is leaving. */
  token: `--${string}`
  fallbackMs: number
}

const OVERLAYS: Record<OverlayKind, { enter: string; exit?: Exit }> = {
  menu: { enter: 'animate-scale-in' },
  dropdown: { enter: 'animate-scale-in-fast origin-top' },
  toast: {
    enter: 'animate-toast-in',
    exit: { className: 'animate-toast-out', token: '--motion-toast-out', fallbackMs: 220 },
  },
  modal: {
    enter: cn(
      'translate-y-0 starting:translate-y-full',
      'lg:starting:translate-y-2 lg:starting:scale-98 lg:starting:opacity-0',
      'transition-[translate,scale,opacity] duration-med ease-out motion-reduce:transition-none',
    ),
  },
  sheet: {
    enter: cn(
      'translate-y-0 starting:translate-y-full',
      'transition-transform duration-med ease-out motion-reduce:transition-none',
    ),
  },
}

interface OverlayMotionOwnProps {
  kind: OverlayKind
  /**
   * True plays the exit and keeps the surface mounted for its length, then
   * renders nothing and calls `onExited`. Only `toast` has an exit; any other
   * kind goes at once. Turning false again mid-exit brings it back.
   */
  leaving?: boolean | undefined
  /** The exit has finished: drop the item it belonged to. */
  onExited?: (() => void) | undefined
  className?: string | undefined
}

/**
 * The in and out of a floating surface: a popover or menu, a toast, a modal or
 * a sheet. Renders the surface's own element (`as`, a `div` by default), so a
 * panel that must not transform — Popover measures its outer box — puts this
 * on an inner wrapper instead.
 */
export default function OverlayMotion<T extends IntrinsicTag = 'div'>({
  as,
  kind,
  leaving = false,
  onExited,
  className,
  ...rest
}: PolymorphicProps<T, OverlayMotionOwnProps>) {
  const { enter, exit } = OVERLAYS[kind]
  const lingering = useLinger(
    !leaving || exit === undefined,
    exit?.token ?? '--motion-med',
    exit?.fallbackMs ?? 0,
  )
  const present = exit === undefined ? !leaving : lingering

  // An effect event: callers pass an inline function, and as a dependency it
  // would call again on every render while the surface is gone.
  const exited = useEffectEvent(() => {
    onExited?.()
  })
  // A layout effect, so the caller drops its item before the empty slot paints.
  useLayoutEffect(() => {
    if (!present) exited()
  }, [present])

  if (!present) return null
  return renderAs(as ?? 'div', {
    ...rest,
    className: cn(leaving && exit ? exit.className : enter, className),
  })
}
